import axios from "axios";
import { get, has } from "lodash";
import {
  CORRECTIONS,
  GAP_FILL_BY_TYPE,
  GAP_FILL_FAMILIES,
  FAMILY_BY_KIND,
} from "./contextOverrides";

// Status-report context labels come from the device type's Schema Registry
// (DDF) schema, served by ND at /v3/settings/schema. For each code, the label is
// resolved in this order:
//   1. CORRECTIONS[type]          firmware-confirmed fixes; win over the DDF
//   2. DDF status_report_context_map
//   3. GAP_FILL_BY_TYPE[type]     used only when the DDF has no entry
//   4. GAP_FILL_FAMILIES[family]  used only when the DDF has no entry
//   5. "Unknown (context N)"
// There is no default table for unlisted types (NDB-5022): borrowing another
// device's labels shows a wrong label instead of an honest unknown.

const CONTEXT_MAP_PATH = [
  "schema",
  "properties",
  "capabilities",
  "properties",
  "device_health_report",
  "properties",
  "props",
  "properties",
  "status_report_context_map",
  "items",
  0,
  "properties",
  "value",
  "const",
];

// One request per device type per page load; concurrent callers share it.
const cache = {};

/**
 * Fetches the DDF status_report_context_map for a device type.
 * Resolves to an object of code -> snake_case DDF value, or null when the type
 * has no schema, no map, or the request fails. Never rejects.
 */
export function loadContextMap(type) {
  if (!type) return Promise.resolve(null);
  if (!cache[type]) {
    cache[type] = axios
      .get(`/v3/settings/schema?type=${encodeURIComponent(type)}`)
      .then((response) => get(response, ["data", ...CONTEXT_MAP_PATH], null))
      .catch(() => {
        // Let a later page load retry instead of caching the failure.
        delete cache[type];
        return null;
      });
  }
  return cache[type];
}

export function clearContextMapCache() {
  Object.keys(cache).forEach((type) => delete cache[type]);
}

const WORDS = {
  ble: "BLE",
  e2ee: "E2EE",
  esc: "ESC",
  fc: "FC",
  gps: "GPS",
  iperf: "iPerf",
  oauth: "OAuth",
  ota: "OTA",
  p2p: "P2P",
  rpc: "RPC",
  tcp: "TCP",
  vod: "VOD",
  wifi: "WiFi",
};

const PHRASES = {
  low_batt_shutdown: "Low Battery Shutdown",
  net_disconnected: "Network Disconnected",
  recording_24_x_7: "24x7 Recording",
};

/** Turns a snake_case DDF value into a display label, e.g. post_ota -> "Post OTA". */
export function humanize(value) {
  const raw = String(value || "");
  if (PHRASES[raw]) return PHRASES[raw];
  return raw
    .split("_")
    .filter(Boolean)
    .map((word) => WORDS[word] || word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** The family lookup key: the type minus any trailing "_vN". */
export function deviceKind(type) {
  const value = type || "";
  if (has(FAMILY_BY_KIND, value)) return value;
  return value.replace(/_v\d+$/i, "");
}

function gapFillFor(type) {
  const family = GAP_FILL_FAMILIES[FAMILY_BY_KIND[deviceKind(type)]] || {};
  return { ...family, ...(GAP_FILL_BY_TYPE[type] || {}) };
}

/**
 * Every labelled code for a device type, merged in lookup order.
 * Returns an object of code (string) -> display label.
 */
export function contextLabels(type, ddfMap) {
  const labels = {};
  Object.entries(gapFillFor(type)).forEach(([code, label]) => {
    labels[code] = label;
  });
  Object.entries(ddfMap || {}).forEach(([code, value]) => {
    labels[code] = humanize(value);
  });
  Object.entries(CORRECTIONS[type] || {}).forEach(([code, label]) => {
    labels[code] = label;
  });
  return labels;
}

/** The display label for one context code. */
export function contextLabel(type, code, ddfMap) {
  const key = String(code);
  const label = contextLabels(type, ddfMap)[key];
  return label || `Unknown (context ${key})`;
}
